import { useCallback, useEffect, useRef, useState } from "react";
import { feedErrorMessage } from "../firebase/feed";
import { createShareLink, currentShareId, revokeShareLink } from "../firebase/share";

export interface ShareLinkState {
  shareId: string | null;
  busy: boolean;
  error: string | null;
  create: (anonymous: boolean) => Promise<boolean>;
  revoke: () => Promise<boolean>;
}

/** The private share link for the debate on screen, and how to change it. */
export function useShareLink(debateId: string | null, enabled: boolean): ShareLinkState {
  const [shareId, setShareId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ignores answers that arrive after the person moved to another debate.
  const current = useRef(debateId);
  current.current = debateId;

  useEffect(() => {
    setShareId(null);
    setError(null);
    setBusy(false);
    if (!debateId || !enabled) return;
    currentShareId(debateId).then((id) => {
      if (current.current === debateId) setShareId(id);
    });
  }, [debateId, enabled]);

  const run = useCallback(async (action: () => Promise<string | null>) => {
    const id = current.current;
    if (!id) return false;
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      if (current.current === id) setShareId(next);
      return true;
    } catch (err) {
      if (current.current === id) setError(feedErrorMessage(err));
      return false;
    } finally {
      if (current.current === id) setBusy(false);
    }
  }, []);

  return {
    shareId,
    busy,
    error,
    create: useCallback(
      (anonymous: boolean) => run(() => createShareLink(current.current!, anonymous)),
      [run],
    ),
    revoke: useCallback(() => run(async () => {
      await revokeShareLink(current.current!);
      return null;
    }), [run]),
  };
}
